import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, adminHeaders, setAdminToken, getAdminToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { KeyRound, Trash2, Plus, LogOut, ListChecks, Settings2, FileText, CheckCircle2, Loader2, Sliders, DollarSign, ShoppingCart, Upload, Download, Database, FileCheck2, FileSpreadsheet } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState("test-ids");
  const [stats, setStats] = useState({ total: 0, completed: 0, active: 0, msosTaken: 0, msisTaken: 0 });
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);

  // generate form
  const [gSub, setGSub] = useState("Institutional");
  const [gInst, setGInst] = useState("");
  const [gEmail, setGEmail] = useState("");
  const [gCount, setGCount] = useState(1);
  const [genLoading, setGenLoading] = useState(false);

  // settings inputs
  const [msisChoices, setMsisChoices] = useState("3, 9");
  const [msosChoices, setMsosChoices] = useState("3, 9");
  const [msisSeconds, setMsisSeconds] = useState(30);
  const [msosSeconds, setMsosSeconds] = useState(30);

  // pricing + orders
  const [pricing, setPricing] = useState(null);
  const [orders, setOrders] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportsLoaded, setReportsLoaded] = useState(false);

  const loadReports = async () => {
    try {
      const { data } = await api.get("/admin/reports", { headers: adminHeaders() });
      setReports(data); setReportsLoaded(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadReportPdf = async (testId) => {
    try {
      const res = await api.get(`/admin/reports/${testId}/pdf`, { headers: adminHeaders(), responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = `Report_${testId}.pdf`; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error("Could not download report"); }
  };

  const openReportPdf = async (testId) => {
    try {
      const res = await api.get(`/admin/reports/${testId}/pdf`, { headers: adminHeaders(), responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(blobUrl, "_blank");
    } catch (e) { toast.error("Could not open report"); }
  };

  useEffect(() => {
    if (!getAdminToken()) { navigate("/admin/login"); return; }
    (async () => {
      try {
        await api.get("/auth/me", { headers: adminHeaders() });
        await refresh();
        setReady(true);
      } catch (e) {
        setAdminToken(null);
        navigate("/admin/login");
      }
    })();
    // eslint-disable-next-line
  }, []);

  const refresh = async () => {
    const [s, r, cfg, pr, ord] = await Promise.all([
      api.get("/admin/stats", { headers: adminHeaders() }),
      api.get("/admin/test-ids", { headers: adminHeaders() }),
      api.get("/admin/settings", { headers: adminHeaders() }),
      api.get("/admin/pricing", { headers: adminHeaders() }),
      api.get("/admin/orders", { headers: adminHeaders() }),
    ]);
    setStats(s.data);
    setRows(r.data);
    setSettings(cfg.data);
    setPricing(pr.data.plans);
    setOrders(ord.data);
    setMsisChoices(cfg.data.MSIS.domainChoices.join(", "));
    setMsosChoices(cfg.data.MSOS.domainChoices.join(", "));
    setMsisSeconds(cfg.data.MSIS.questionSeconds || 30);
    setMsosSeconds(cfg.data.MSOS.questionSeconds || 30);
  };

  const logout = () => { setAdminToken(null); navigate("/admin/login"); };

  const generate = async () => {
    if (!gInst.trim() || !gEmail.trim()) { toast.error("Institution and candidate email are required."); return; }
    setGenLoading(true);
    try {
      const { data } = await api.post("/admin/test-ids",
        { subscriptionType: gSub, institution: gInst.trim(), candidateEmail: gEmail.trim(), count: Number(gCount) || 1 },
        { headers: adminHeaders() });
      toast.success(`${data.created.length} ${gSub} Test ID(s) generated`);
      setGInst(""); setGEmail(""); setGCount(1);
      await refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setGenLoading(false); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/test-ids/${id}`, { headers: adminHeaders() });
      toast.success("Test ID deleted");
      await refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveSettings = async (testType, raw, seconds) => {
    const choices = raw.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n));
    try {
      await api.put("/admin/settings", { testType, domainChoices: choices, questionSeconds: Number(seconds) || 30 }, { headers: adminHeaders() });
      toast.success(`${testType} settings updated`);
      await refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setAmt = (key, val) => setPricing((p) => ({ ...p, [key]: { ...p[key], amount: Number(val) || 0 } }));
  const savePricing = async () => {
    try {
      await api.put("/admin/pricing", { plans: pricing }, { headers: adminHeaders() });
      toast.success("Pricing updated");
      await refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  if (!ready) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-slate-900 text-white flex flex-col">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
          <div className="w-8 h-8 rounded-md bg-white grid place-items-center text-slate-900 font-bold font-head">M</div>
          <span className="font-head font-bold">MSI Scale</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <SideBtn testid={ADMIN.navTestIds} active={tab === "test-ids"} onClick={() => setTab("test-ids")} icon={ListChecks}>Test IDs</SideBtn>
          <SideBtn testid="admin-nav-reports" active={tab === "reports"} onClick={() => { setTab("reports"); loadReports(); }} icon={FileCheck2}>Reports</SideBtn>
          <SideBtn testid="admin-nav-institute" active={tab === "institute"} onClick={() => setTab("institute")} icon={FileSpreadsheet}>Institute Export</SideBtn>
          <SideBtn testid={ADMIN.navOrders} active={tab === "orders"} onClick={() => setTab("orders")} icon={ShoppingCart}>Orders</SideBtn>
          <SideBtn testid={ADMIN.navPricing} active={tab === "pricing"} onClick={() => setTab("pricing")} icon={DollarSign}>Pricing</SideBtn>
          <SideBtn testid={ADMIN.navSettings} active={tab === "settings"} onClick={() => setTab("settings")} icon={Settings2}>Domain Settings</SideBtn>
          <SideBtn testid="admin-nav-content" active={tab === "content"} onClick={() => setTab("content")} icon={Database}>Content</SideBtn>
        </nav>
        <div className="p-3 border-t border-white/10">
          <button data-testid={ADMIN.logoutButton} onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-slate-300 hover:bg-white/10 hover:text-white transition-colors text-sm">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-auto">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-8">
          <h1 className="font-head font-bold text-lg">{({ "test-ids": "Test IDs", reports: "Reports", institute: "Institute Export", orders: "Orders", pricing: "Pricing", settings: "Domain Settings", content: "Content" })[tab]}</h1>
        </header>

        <div className="p-8">
          {tab === "test-ids" && (
            <>
              {/* KPI strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Kpi icon={KeyRound} label="Total IDs" value={stats.total} />
                <Kpi icon={CheckCircle2} label="Completed" value={stats.completed} />
                <Kpi icon={FileText} label="MSOS Taken" value={stats.msosTaken} />
                <Kpi icon={FileText} label="MSIS Taken" value={stats.msisTaken} />
              </div>

              {/* Generate form */}
              <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8">
                <div className="flex items-center gap-2 mb-5"><Plus className="w-4 h-4 text-blue-700" /><h2 className="font-semibold">Generate Test IDs</h2></div>
                <div className="grid md:grid-cols-5 gap-4 items-end">
                  <div>
                    <Label className="mb-1.5 block text-xs">Subscription</Label>
                    <Select value={gSub} onValueChange={setGSub}>
                      <SelectTrigger data-testid={ADMIN.genSubscription}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Institutional">Institutional</SelectItem>
                        <SelectItem value="Individual">Individual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-xs">Institution</Label>
                    <Input data-testid={ADMIN.genInstitution} value={gInst} onChange={(e) => setGInst(e.target.value)} placeholder="e.g. IIM Bangalore" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-xs">Candidate email</Label>
                    <Input data-testid={ADMIN.genEmail} type="email" value={gEmail} onChange={(e) => setGEmail(e.target.value)} placeholder="candidate@email.com" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-xs">Count</Label>
                    <Input data-testid={ADMIN.genCount} type="number" min={1} max={100} value={gCount} onChange={(e) => setGCount(e.target.value)} />
                  </div>
                  <Button data-testid={ADMIN.generateButton} onClick={generate} disabled={genLoading}
                    className="h-10 rounded-full bg-blue-700 hover:bg-blue-800 text-white">
                    {genLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Generate
                  </Button>
                </div>
              </div>

              {/* Table */}
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <Table data-testid={ADMIN.table}>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Test ID</TableHead>
                      <TableHead>Subscription</TableHead>
                      <TableHead>Institution</TableHead>
                      <TableHead>Date Generated</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No Test IDs yet. Generate one above.</TableCell></TableRow>
                    )}
                    {rows.map((r) => (
                      <TableRow key={r.id} data-testid={ADMIN.tableRow}>
                        <TableCell className="font-mono font-semibold">{r.testId}</TableCell>
                        <TableCell><Badge variant="outline" className={r.subscriptionType === "Institutional" ? "border-blue-300 text-blue-700" : "border-slate-300 text-slate-600"}>{r.subscriptionType}</Badge></TableCell>
                        <TableCell>{r.institution}</TableCell>
                        <TableCell className="text-slate-600">{fmtDate(r.createdAt)}</TableCell>
                        <TableCell>
                          {r.status === "completed"
                            ? <span className="inline-flex items-center gap-1 text-emerald-700 text-sm font-medium"><CheckCircle2 className="w-4 h-4" /> Completed{r.takenTestType ? ` · ${r.takenTestType}` : ""}</span>
                            : <span className="inline-flex items-center gap-1 text-amber-600 text-sm font-medium">Active</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button data-testid={`${ADMIN.deleteButton}-${r.testId}`} variant="ghost" size="icon" className="text-slate-400 hover:text-red-600 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Test ID {r.testId}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This permanently removes the Test ID{r.status === "completed" ? " and its stored report" : ""}. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction data-testid={ADMIN.confirmDelete} onClick={() => del(r.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          {tab === "reports" && (
            <div>
              <div className="mb-5">
                <h2 className="text-lg font-semibold">Completed Reports</h2>
                <p className="text-slate-500 text-sm">Open or download the PDF report for any candidate who has finished an assessment.</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Candidate</TableHead>
                      <TableHead>Institution</TableHead>
                      <TableHead>Test</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Top Domain</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead className="text-right">Report</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportsLoaded && reports.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No completed reports yet.</TableCell></TableRow>
                    )}
                    {reports.map((r) => (
                      <TableRow key={r.testId} data-testid={`report-row-${r.testId}`}>
                        <TableCell>
                          <div className="font-medium text-slate-800">{r.candidateEmail || "—"}</div>
                          <div className="font-mono text-xs text-slate-400">{r.testId}</div>
                        </TableCell>
                        <TableCell className="text-slate-600">{r.institution}</TableCell>
                        <TableCell><Badge variant="outline" className={r.testType === "MSIS" ? "border-purple-300 text-purple-700" : "border-blue-300 text-blue-700"}>{r.testType}</Badge></TableCell>
                        <TableCell className="text-slate-600">{fmtDate(r.completedAt)}</TableCell>
                        <TableCell className="text-slate-700">{r.recommended || "—"}</TableCell>
                        <TableCell className="text-slate-700">{r.overallScore != null ? `${r.overallScore}/${r.overallMax} (${r.overallPercentage}%)` : "—"}</TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex gap-1">
                            <Button data-testid={`report-view-${r.testId}`} variant="ghost" size="sm" className="text-slate-500 hover:text-blue-700" onClick={() => openReportPdf(r.testId)}>
                              <FileText className="w-4 h-4 mr-1" /> View
                            </Button>
                            <Button data-testid={`report-download-${r.testId}`} variant="outline" size="sm" className="rounded-full" onClick={() => downloadReportPdf(r.testId)}>
                              <Download className="w-4 h-4 mr-1" /> PDF
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {tab === "settings" && settings && (
            <div className="max-w-2xl space-y-6">
              <p className="text-slate-500">Control how many domains a test taker may select before beginning. Enter allowed options as comma-separated numbers (1–9), e.g. <span className="font-mono">3, 9</span>.</p>
              <SettingsCard title="MSOS — Orientation Scale" value={msosChoices} setValue={setMsosChoices}
                secondsValue={msosSeconds} setSeconds={setMsosSeconds} currentSeconds={settings.MSOS.questionSeconds}
                inputTestid={ADMIN.settingsMsosInput} saveTestid={ADMIN.settingsMsosSave}
                onSave={() => saveSettings("MSOS", msosChoices, msosSeconds)} current={settings.MSOS.domainChoices} />
              <SettingsCard title="MSIS — Inclination Scale" value={msisChoices} setValue={setMsisChoices}
                secondsValue={msisSeconds} setSeconds={setMsisSeconds} currentSeconds={settings.MSIS.questionSeconds}
                inputTestid={ADMIN.settingsMsisInput} saveTestid={ADMIN.settingsMsisSave}
                onSave={() => saveSettings("MSIS", msisChoices, msisSeconds)} current={settings.MSIS.domainChoices} />
            </div>
          )}

          {tab === "pricing" && pricing && (
            <div className="max-w-2xl space-y-5">
              <p className="text-slate-500">Edit the price charged at checkout. Amounts are per test in the plan's own currency. Each purchased Test ID works for either MSIS or MSOS.</p>
              <PriceRow label="Individual · India (₹ per test)" testid={ADMIN.priceIndividualInr} value={pricing.individual_inr.amount} onChange={(v) => setAmt("individual_inr", v)} />
              <PriceRow label="Institutional · India (₹ per test)" testid="pricing-institution-inr-input" value={pricing.institution_inr.amount} onChange={(v) => setAmt("institution_inr", v)} />
              <PriceRow label="Individual · International ($ per test)" testid={ADMIN.priceIndividualUsd} value={pricing.individual_usd.amount} onChange={(v) => setAmt("individual_usd", v)} />
              <PriceRow label="Institutional · International ($ per test)" testid={ADMIN.priceInstitutionUsd} value={pricing.institution_usd.amount} onChange={(v) => setAmt("institution_usd", v)} />
              <Button data-testid={ADMIN.pricingSave} onClick={savePricing} className="rounded-full bg-blue-700 hover:bg-blue-800 text-white">Save pricing</Button>
            </div>
          )}

          {tab === "orders" && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <Table data-testid={ADMIN.ordersTable}>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Buyer</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Test IDs</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No orders yet.</TableCell></TableRow>
                  )}
                  {orders.map((o) => (
                    <TableRow key={o.id}>
                      <TableCell className="text-slate-600">{fmtDate(o.createdAt)}</TableCell>
                      <TableCell><div className="font-medium">{o.name}</div><div className="text-xs text-slate-400">{o.email}</div></TableCell>
                      <TableCell><Badge variant="outline">{o.plan}</Badge></TableCell>
                      <TableCell>{o.quantity}</TableCell>
                      <TableCell className="font-semibold">{o.currency} {o.amount}</TableCell>
                      <TableCell>
                        {o.status === "paid"
                          ? <span className="inline-flex items-center gap-1 text-emerald-700 text-sm font-medium"><CheckCircle2 className="w-4 h-4" /> Paid</span>
                          : <span className="text-amber-600 text-sm font-medium">Created</span>}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{(o.testIds || []).join(", ") || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {tab === "institute" && <InstituteExportPanel />}

          {tab === "content" && (
            <div className="max-w-3xl space-y-6">
              <p className="text-slate-500">Replace the question banks by uploading an Excel (.xlsx) or CSV file. Download the current bank, edit it, then upload to publish. Changes take effect immediately for new test-takers.</p>
              <ContentCard testType="MSOS" title="MSOS — Orientation Scale (MCQ, right/wrong)" />
              <ContentCard testType="MSIS" title="MSIS — Inclination Scale (Likert 1–5)" />
            </div>
          )}

        </div>
      </main>
    </div>
  );
}

function SideBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
        active ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
      }`}>
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-center gap-2 text-slate-400 mb-2"><Icon className="w-4 h-4" /><span className="eyebrow">{label}</span></div>
      <div className="text-3xl font-bold">{value}</div>
    </div>
  );
}

function SettingsCard({ title, value, setValue, onSave, current, inputTestid, saveTestid, secondsValue, setSeconds, currentSeconds }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4"><Sliders className="w-4 h-4 text-blue-700" /><h3 className="font-semibold">{title}</h3></div>
      <Label className="mb-1.5 block text-xs">Allowed domain-count options</Label>
      <Input data-testid={inputTestid} value={value} onChange={(e) => setValue(e.target.value)} placeholder="3, 9" />
      <p className="text-xs text-slate-400 mt-1.5">Currently: {current.join(", ")} domains</p>

      <Label className="mb-1.5 block text-xs mt-5">Seconds per question (auto-advances at 0)</Label>
      <Input data-testid={`${inputTestid}-seconds`} type="number" min={5} max={600} value={secondsValue} onChange={(e) => setSeconds(e.target.value)} className="w-40" />
      <p className="text-xs text-slate-400 mt-1.5">Currently: {currentSeconds || 30}s per question</p>

      <Button data-testid={saveTestid} onClick={onSave} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white mt-5">Save settings</Button>
    </div>
  );
}

function PriceRow({ label, value, onChange, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 flex items-center justify-between gap-4">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <Input data-testid={testid} type="number" value={value} onChange={(e) => onChange(e.target.value)} className="w-36" />
    </div>
  );
}

function InstituteExportPanel() {
  const [q, setQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [testType, setTestType] = useState("");
  const [rows, setRows] = useState(null);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);

  const buildParams = () => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (fromDate) p.set("from_date", fromDate);
    if (testType) p.set("test_type", testType);
    return p.toString();
  };

  const preview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/institute-export/preview?${buildParams()}`, { headers: adminHeaders() });
      setRows(data.rows); setCount(data.count);
      if (data.count === 0) toast.info("No completed test-takers match these filters.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const { data: chk } = await api.get(`/admin/institute-export/preview?${buildParams()}`, { headers: adminHeaders() });
      if (chk.count === 0) {
        toast.warning("No completed test-takers match these filters — downloading a headers-only Excel. Try clearing or moving the 'Generated on or after' date earlier.");
      }
      const res = await api.get(`/admin/institute-export?${buildParams()}`, { headers: adminHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `institute_results_${(q.trim() || "all").replace(/\s+/g, "_")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl space-y-6">
      <p className="text-slate-500">Export completed test-takers of an institute to Excel — one test-taker per row. Filter by the institute name or the email used when the Test IDs were generated, and optionally from a generation date to skip older batches.</p>

      <div className="bg-white border border-slate-200 rounded-lg p-6 grid sm:grid-cols-4 gap-4 items-end">
        <div className="sm:col-span-2">
          <Label className="mb-1.5 block text-xs">Institute name or email (used at generation)</Label>
          <Input data-testid="institute-export-query" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. IBS Hyderabad or kumar@ibsindia.org" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Generated on or after</Label>
          <Input data-testid="institute-export-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Test type</Label>
          <select data-testid="institute-export-type" value={testType} onChange={(e) => setTestType(e.target.value)}
            className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
            <option value="">All</option>
            <option value="MSOS">MSOS</option>
            <option value="MSIS">MSIS</option>
          </select>
        </div>
        <div className="sm:col-span-4 flex gap-3">
          <Button data-testid="institute-export-preview" onClick={preview} disabled={busy} variant="outline" className="rounded-full">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ListChecks className="w-4 h-4 mr-1" />} Preview
          </Button>
          <Button data-testid="institute-export-download" onClick={download} disabled={busy} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white">
            <Download className="w-4 h-4 mr-1" /> Download Excel
          </Button>
        </div>
      </div>

      {rows && (
        <div>
          <p className="text-sm text-slate-500 mb-3" data-testid="institute-export-count">{count} completed test-taker(s) matched{count > 300 ? " (showing first 300 in preview; full set is in the Excel)" : ""}.</p>
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Test ID</TableHead>
                  <TableHead>Test-taker</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Institute</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Primary Specialization</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">No matches.</TableCell></TableRow>}
                {rows.map((r) => (
                  <TableRow key={r.testId} data-testid={`institute-row-${r.testId}`}>
                    <TableCell>{r.sno}</TableCell>
                    <TableCell className="font-mono text-xs">{r.testId}</TableCell>
                    <TableCell className="text-slate-600">{r.takerEmail}</TableCell>
                    <TableCell><Badge variant="outline" className={r.testType === "MSIS" ? "border-purple-300 text-purple-700" : "border-blue-300 text-blue-700"}>{r.testType}</Badge></TableCell>
                    <TableCell className="text-slate-600">{r.institute}</TableCell>
                    <TableCell className="text-slate-600">{r.dateCompleted}</TableCell>
                    <TableCell className="text-slate-800">{r.primaryDomain}</TableCell>
                    <TableCell className="text-slate-700">{r.primaryScore}</TableCell>
                    <TableCell className="text-slate-700">{r.primaryPct}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

function ContentCard({ testType, title }) {
  const [summary, setSummary] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileObj, setFileObj] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);

  const loadSummary = async () => {
    try {
      const { data } = await api.get(`/admin/content/${testType}`, { headers: adminHeaders() });
      setSummary(data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadSummary(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (kind, format) => {
    const fname = `${testType}_${kind === "export" ? "item_bank" : "template"}.${format}`;
    try {
      const res = await api.get(`/admin/content/${testType}/${kind}?format=${format}`, { headers: adminHeaders(), responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error("Download failed"); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileObj(f); setFileName(f.name); setPreview(null); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/admin/content/${testType}/preview`, fd, { headers: adminHeaders() });
      setPreview(data);
    } catch (e2) { toast.error(apiError(e2)); } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!fileObj) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", fileObj);
      const { data } = await api.post(`/admin/content/${testType}/upload`, fd, { headers: adminHeaders() });
      toast.success(`${testType} bank published — ${data.counts.items} items · ${data.counts.domains} domains`);
      setFileObj(null); setFileName(""); setPreview(null);
      await loadSummary();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid={`content-card-${testType}`}>
      <div className="flex items-center gap-2 mb-4"><FileText className="w-4 h-4 text-blue-700" /><h3 className="font-semibold">{title}</h3></div>
      {summary && (
        <div data-testid={`content-summary-${testType}`} className="flex flex-wrap gap-4 text-sm text-slate-600 mb-5">
          <span><b className="text-slate-900">{summary.domains}</b> domains</span>
          <span><b className="text-slate-900">{summary.constructs}</b> constructs</span>
          <span><b className="text-slate-900">{summary.items}</b> items</span>
          {summary.updatedAt && <span className="text-slate-400">Updated {new Date(summary.updatedAt).toLocaleString()}</span>}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-5">
        <Button data-testid={`content-download-current-xlsx-${testType}`} variant="outline" size="sm" className="rounded-full" onClick={() => download("export", "xlsx")}>
          <Download className="w-4 h-4 mr-1" /> Current (Excel)
        </Button>
        <Button data-testid={`content-download-current-csv-${testType}`} variant="outline" size="sm" className="rounded-full" onClick={() => download("export", "csv")}>
          <Download className="w-4 h-4 mr-1" /> Current (CSV)
        </Button>
        <Button data-testid={`content-download-template-xlsx-${testType}`} variant="ghost" size="sm" className="rounded-full text-slate-500" onClick={() => download("template", "xlsx")}>
          Blank template (Excel)
        </Button>
        <Button data-testid={`content-download-template-csv-${testType}`} variant="ghost" size="sm" className="rounded-full text-slate-500" onClick={() => download("template", "csv")}>
          Blank template (CSV)
        </Button>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <Label className="mb-1.5 block text-xs">Upload a new bank (.xlsx or .csv) — this replaces the entire {testType} bank</Label>
        <input data-testid={`content-upload-input-${testType}`} type="file" accept=".xlsx,.csv"
          onChange={onFile} className="block text-sm text-slate-600 file:mr-3 file:rounded-full file:border-0 file:bg-slate-900 file:text-white file:px-4 file:py-2 file:text-sm file:cursor-pointer" />
        {fileName && <p className="text-xs text-slate-400 mt-2">Selected: {fileName}</p>}

        {busy && <p className="text-sm text-slate-500 mt-3 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Validating…</p>}

        {preview && !preview.valid && (
          <div data-testid={`content-errors-${testType}`} className="mt-3 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-700 mb-2">This file has {preview.errors.length} issue(s) and was not applied:</p>
            <ul className="text-xs text-red-600 space-y-1 max-h-40 overflow-auto list-disc pl-4">
              {preview.errors.map((er, i) => <li key={i}>{er}</li>)}
            </ul>
          </div>
        )}

        {preview && preview.valid && (
          <div data-testid={`content-preview-${testType}`} className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-sm font-semibold text-emerald-800 mb-1">File looks good — ready to publish</p>
            <p className="text-sm text-emerald-700">{preview.counts.domains} domains · {preview.counts.constructs} constructs · {preview.counts.items} items</p>
            <Button data-testid={`content-confirm-upload-${testType}`} onClick={commit} disabled={busy}
              className="mt-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
              <Upload className="w-4 h-4 mr-1" /> Replace {testType} bank
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}